/**
 * ItemProvenanceSheet — Plan Spine
 *
 * One tap on any plan item answers "who agreed to this?": real names, real
 * dates, the process behind the agreement, and — because a plan has no edit
 * button — the version chain, shown honestly whether or not this is still
 * the current version.
 *
 * Phone-first bottom sheet. Pure presentation: it renders an already-
 * assembled `ItemProvenance` record (see `../lib/provenance.ts`) and never
 * fetches anything itself. Plain language only — this file must never
 * render the words "loop", "POR", "S2", "catchball", or "RPC".
 *
 * Currently unmounted — a teammate's page wires this up. Exported cleanly
 * so it can be dropped in with just `{ provenance, onClose }`.
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckCircle2, Clock, FileQuestion, GitBranch, Users, X } from 'lucide-react';
import type { ItemProvenance, ProvenanceActor } from '../lib/provenance';

export interface ItemProvenanceSheetProps {
  provenance: ItemProvenance;
  onClose: () => void;
}

// ── Date formatting ─────────────────────────────────────────────────────────
// Fixed to UTC: these are one-time recorded timestamps shared across every
// viewer's timezone. Using the browser's local zone can shift the displayed
// day near midnight (a real "Mar 1" reading as "Feb 28" for a viewer behind
// UTC).

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Ratified by Jane Lee · Mar 10, 2026" — degrades to whichever half is on record, or null when neither is. */
function actorLine(actor: ProvenanceActor, verb: string): string | null {
  const when = fmtDate(actor.date);
  if (actor.name && when) return `${verb} by ${actor.name} · ${when}`;
  if (actor.name) return `${verb} by ${actor.name}`;
  if (when) return `${verb} · ${when}`;
  return null;
}

// ── Small presentational primitives ─────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
      {children}
    </p>
  );
}

function NoRecord({ children = 'No record' }: { children?: React.ReactNode }) {
  return <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">{children}</p>;
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <SectionLabel>{label}</SectionLabel>
        {children}
      </div>
    </div>
  );
}

// ── Sheet ────────────────────────────────────────────────────────────────────

export function ItemProvenanceSheet({ provenance, onClose }: ItemProvenanceSheetProps) {
  const {
    itemTitle,
    ratifiedBy,
    evidenceSummary,
    valueSource,
    proposalChain,
    version,
    override,
    exchangeSummary,
    hasAnyRecord,
  } = provenance;

  const ratifiedLine = actorLine(ratifiedBy, 'Ratified');
  const submittedLine = proposalChain ? actorLine(proposalChain.submittedBy, 'Submitted') : null;
  const acceptedLine = proposalChain ? actorLine(proposalChain.acceptedBy, 'Accepted') : null;
  const replacedByDate = version.replacedBy ? fmtDate(version.replacedBy.date) : null;
  const replacesDate = version.replaces ? fmtDate(version.replaces.date) : null;
  const overrideLine = override ? actorLine(override.authorizedBy, 'Authorized') : null;

  return (
    <Transition.Root show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-y-full opacity-0 sm:translate-y-4"
            enterTo="translate-y-0 opacity-100"
            leave="ease-in duration-200"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-full opacity-0 sm:translate-y-4"
          >
            <Dialog.Panel className="flex max-h-[88vh] w-full flex-col overflow-hidden border border-[var(--rule)]/80 bg-[var(--bg-raised)] shadow-2xl dark:border-[var(--rule)] dark:bg-[var(--surface)] sm:max-w-lg sm:">
              {/* Drag handle affordance (phone-first) */}
              <div className="flex justify-center pt-2 pb-1 sm:hidden">
                <div className="h-1.5 w-10 rounded-full bg-[var(--text-ter)] dark:bg-[var(--rule-bright)]" />
              </div>

              {/* Header */}
              <div className="flex items-start justify-between gap-3 border-b border-[var(--rule)] px-5 py-4 dark:border-[var(--rule)]">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    Who agreed to this
                  </p>
                  <Dialog.Title className="mt-0.5 truncate text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {itemTitle ?? 'This item'}
                  </Dialog.Title>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex-shrink-0 p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-5">
                {!hasAnyRecord ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <FileQuestion className="h-8 w-8 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                    <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                      No record of how this was decided yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* Supersession — never hidden, always the first thing shown when it applies */}
                    {version.replacedBy && (
                      <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 dark:border-[var(--mustard)]/60 dark:bg-[var(--mustard)]/20">
                        <p className="text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                          A newer version (v{version.replacedBy.version}) has since replaced this one
                          {replacedByDate ? ` · ${replacedByDate}` : ''}.
                        </p>
                      </div>
                    )}

                    {/* Ratification */}
                    <InfoRow icon={<CheckCircle2 className="h-4 w-4" />} label="Ratified">
                      {ratifiedLine ? (
                        <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{ratifiedLine}</p>
                      ) : (
                        <NoRecord />
                      )}
                    </InfoRow>

                    {/* Evidence + value source */}
                    {(evidenceSummary || valueSource) && (
                      <InfoRow icon={<Clock className="h-4 w-4" />} label="Based on">
                        <div className="space-y-0.5">
                          {evidenceSummary ? (
                            <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{evidenceSummary}</p>
                          ) : (
                            <NoRecord />
                          )}
                          {valueSource && (
                            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{valueSource}</p>
                          )}
                        </div>
                      </InfoRow>
                    )}

                    {/* Proposal chain */}
                    <InfoRow icon={<Users className="h-4 w-4" />} label="Proposal">
                      {submittedLine || acceptedLine ? (
                        <div className="space-y-0.5">
                          <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                            {submittedLine ?? <NoRecord>No submission on record</NoRecord>}
                          </p>
                          <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                            {acceptedLine ?? <NoRecord>No acceptance on record</NoRecord>}
                          </p>
                        </div>
                      ) : (
                        <NoRecord />
                      )}
                    </InfoRow>

                    {/* Exchange summary — the process behind the agreement, at a glance */}
                    {exchangeSummary.length > 0 && (
                      <div>
                        <SectionLabel>How this was decided</SectionLabel>
                        <ol className="space-y-2 border-l-2 border-[var(--rule)] pl-4 dark:border-[var(--rule)]">
                          {exchangeSummary.map((line, i) => (
                            <li key={i} className="relative text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary-400" />
                              {line}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* This item's own override, when one applies */}
                    {override && (
                      <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3 dark:border-[var(--slate)]/60 dark:bg-[var(--slate)]/20">
                        <p className="text-sm font-medium text-[var(--text-sec)]">
                          This item's rank was changed by an authorized override
                          {override.previousRank != null && override.newRank != null
                            ? ` — from #${override.previousRank} to #${override.newRank}`
                            : ''}
                          .
                        </p>
                        {overrideLine ? (
                          <p className="mt-1 text-sm text-[var(--text-sec)]">{overrideLine}</p>
                        ) : (
                          <p className="mt-1 text-sm italic text-[var(--text-sec)]">
                            No record of who authorized it.
                          </p>
                        )}
                        {override.justification && (
                          <p className="mt-1 text-sm text-[var(--text-sec)]">{override.justification}</p>
                        )}
                      </div>
                    )}

                    {/* Version detail */}
                    <InfoRow icon={<GitBranch className="h-4 w-4" />} label="Version">
                      <div className="space-y-0.5">
                        <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                          {version.version === 1
                            ? 'This is the first version of this plan.'
                            : `This is version ${version.version} of this plan.`}
                        </p>
                        {version.replaces && (
                          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                            It replaced version {version.replaces.version}
                            {replacesDate ? ` (ratified ${replacesDate})` : ''}.
                          </p>
                        )}
                        {version.changeReason && (
                          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{version.changeReason}</p>
                        )}
                      </div>
                    </InfoRow>
                  </div>
                )}
              </div>

              {/* Footer — the honesty guarantee */}
              <div className="border-t border-[var(--rule)] px-5 py-3 dark:border-[var(--rule)]">
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  "No record" means it wasn't captured — never a guess.
                </p>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
